/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, type KeyboardEvent } from 'react';

import { type MessageDescriptor, FormattedMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { CyphieMode } from '../../../../../../../state/LabsAtoms';
import { messages } from './messages';
import { ModeIcon } from './ModeIcon';

const styles = cssMap({
	popupContent: {
		overflow: 'hidden',
		minWidth: '180px',
	},
	menuItem: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	menuItemSelected: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		cursor: 'pointer',
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
	menuItemContent: {
		display: 'flex',
		flexDirection: 'column',
	},
});

export const MODE_LABELS: Record<CyphieMode, MessageDescriptor> = {
	default: messages.defaultLabel,
	reasoning: messages.reasoningLabel,
};

const MODE_DESCRIPTIONS: Record<CyphieMode, MessageDescriptor> = {
	default: messages.defaultDescription,
	reasoning: messages.reasoningDescription,
};

const AVAILABLE_MODES = ['default', 'reasoning'] as const satisfies CyphieMode[];

type PopupContentProps = {
	selectedMode: CyphieMode;
	external?: boolean;
	onModeSelect: (mode: CyphieMode) => void;
	testId?: string;
};

export const PopupContent = ({
	selectedMode,
	external = false,
	onModeSelect,
	testId = 'mode-selector-popup-content',
}: PopupContentProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const handleModeSelect = useCallback(
		(mode: CyphieMode) => {
			onModeSelect(mode);
		},
		[onModeSelect],
	);

	const handleOptionKeyDown = useCallback(
		(mode: CyphieMode) => (event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key === 'Enter' || event.key === ' ') {
				handleModeSelect(mode);
			}
		},
		[handleModeSelect],
	);

	return (
		<Box
			xcss={styles.popupContent}
			role="listbox"
			aria-label={formatMessage(messages.selectModelLabel)}
			testId={testId}
		>
			{AVAILABLE_MODES.map((mode) => (
				<Box
					key={mode}
					role="option"
					aria-selected={selectedMode === mode}
					onClick={() => handleModeSelect(mode)}
					onKeyDown={handleOptionKeyDown(mode)}
					tabIndex={0}
					testId={`${testId}-option-${mode}`}
				>
					<Box xcss={selectedMode === mode ? styles.menuItemSelected : styles.menuItem}>
						<ModeIcon mode={mode} />
						<Box xcss={styles.menuItemContent}>
							<Text weight="medium">
								<FormattedMessage
									{...(external && mode === 'default' ? messages.fastLabel : MODE_LABELS[mode])}
								/>
							</Text>
							<Text size="small" color="color.text.subtlest">
								<FormattedMessage {...MODE_DESCRIPTIONS[mode]} />
							</Text>
						</Box>
					</Box>
				</Box>
			))}
		</Box>
	);
};
